import { type FC, useCallback, useEffect, useRef, useState } from "react";

import Lottie from "react-lottie";

import { useQueryClient } from "@tanstack/react-query";

import { cn } from "@/shared/utils/core";
import { supabase } from "@/shared/lib/supabase";
import { TASK_IDS, INTAKE_TASK_IDS } from "@/shared/constants/taskIds";

import { TriageModalSidebar } from "./TriageModalSidebar";

import paperplaneAnimation from "@/shared/assets/animations/paperplane.json";

const MESSAGES = [
  "Confirming who has authority",
  "Finding your county's requirements",
  "Matching you to your starting point",
];

const TOTAL_DURATION_MS = 5000;
const MESSAGE_INTERVAL = Math.floor(TOTAL_DURATION_MS / MESSAGES.length);

const ESTATE_SETUP_TASKS = [
  TASK_IDS.WILL,
  TASK_IDS.TRUST,
  TASK_IDS.LOT,
  INTAKE_TASK_IDS.INTRO,
] as const;

interface TriageModalProcessingStepProps {
  estateId: number;
  onComplete: () => void;
}

export const TriageModalProcessingStep: FC<TriageModalProcessingStepProps> = ({
  estateId,
  onComplete,
}) => {
  const [messageIndex, setMessageIndex] = useState(0);
  const [visible, setVisible] = useState(true);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const queryClient = useQueryClient();
  const upsertDone = useRef(false);
  const timerDone = useRef(false);

  const tryComplete = useCallback(() => {
    if (upsertDone.current && timerDone.current) {
      onCompleteRef.current();
    }
  }, []);

  useEffect(() => {
    const now = new Date().toISOString();
    const rows = ESTATE_SETUP_TASKS.map((taskId) => ({
      estate_id: estateId,
      task_id: taskId,
      status: "completed",
      completed_at: now,
      last_active_at: now,
    }));

    supabase
      .from("estate_tasks")
      .upsert(rows, { onConflict: "estate_id,task_id" })
      .then(({ error }) => {
        if (error) {
          console.error("Failed to complete estate tasks:", error);
        } else {
          queryClient.invalidateQueries({ queryKey: ["moduleProgress", estateId] });
        }
        upsertDone.current = true;
        tryComplete();
      });
  }, [estateId, queryClient, tryComplete]);

  useEffect(() => {
    const interval = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setMessageIndex((prev) => {
          const next = prev + 1;
          if (next >= MESSAGES.length) {
            clearInterval(interval);
            return prev;
          }
          setVisible(true);
          return next;
        });
      }, 300);
    }, MESSAGE_INTERVAL);

    const completeTimer = setTimeout(() => {
      timerDone.current = true;
      tryComplete();
    }, TOTAL_DURATION_MS);

    return () => {
      clearInterval(interval);
      clearTimeout(completeTimer);
    };
  }, [tryComplete]);

  return (
    <div className="flex h-full">
      <TriageModalSidebar activeStep={4} />

      <div className="flex min-w-0 flex-1 flex-col items-center justify-center bg-subtle px-5 sm:px-8">
        <div className="flex flex-col items-center gap-4">
          <div className="h-[172px] w-[172px]">
            <Lottie
              options={{
                loop: true,
                autoplay: true,
                animationData: paperplaneAnimation,
                rendererSettings: { preserveAspectRatio: "xMidYMid slice" },
              }}
              isClickToPauseDisabled
            />
          </div>
          <p
            className={cn(
              "text-sm font-medium text-primary transition-all duration-300",
              visible ? "translate-y-0 opacity-100" : "translate-y-2 opacity-0",
            )}
          >
            {MESSAGES[messageIndex]}
          </p>
        </div>
      </div>
    </div>
  );
};
